import * as THREE from 'three';

/**
 * Global time-of-day + weather state. Centralises everything so renderers
 * (sun/fog, grass wind, rain particles) just read from `state` every frame.
 *
 * Timeline conventions:
 *   timeOfDay ∈ [0, 1) — 0 = midnight, 0.25 = sunrise, 0.5 = noon, 0.75 = sunset.
 *   dayLength = seconds for one full cycle (default 240s = 4 minutes).
 */
export interface WeatherState {
  timeOfDay: number;
  /** Unit vector FROM sun TO origin, so renderers just copy this into sun.position.clone().negate() style. */
  sunDir: THREE.Vector3;
  /** Directional-light intensity for the current sun angle (0 at night). */
  sunIntensity: number;
  /** Warm/cool tint for sunlight — oranges at sunrise/sunset, whitish at noon, cool at night. */
  sunColor: THREE.Color;
  ambientIntensity: number;
  skyColor: THREE.Color;
  fogColor: THREE.Color;
  fogDensity: number;
  environmentIntensity: number;

  /** Active rain, 0 = clear, 1 = storming. Smooth ramp in/out. */
  rain: number;

  /** Grass wind multiplier (1 = baseline breeze). Peaks during gusts. */
  wind: number;

  /** Fractional year position in [0, 1). 0 = Jan 1, 0.5 = Jul 2, etc. */
  yearT: number;
  /** Integer day-of-year [1..365] for display. */
  dayOfYear: number;
  /** Readable label for the current season. */
  season: 'winter' | 'spring' | 'summer' | 'autumn';
  /** Ambient outdoor temperature (°C). Function of season + time of day. */
  temperature: number;

  /** Once-per-year survival events. 'idle' = no event scheduled;
   *  'warning' = imminent (HUD countdown banner shown, recall fires);
   *  'active' = damage on, colony hidden underground. Cold = winter
   *  only, heat = summer only. */
  coldSnapPhase: 'idle' | 'warning' | 'active';
  coldSnapT: number;
  heatWavePhase: 'idle' | 'warning' | 'active';
  heatWaveT: number;
}

interface WeatherOptions {
  dayLength?: number;
  initialTimeOfDay?: number;
  rainMinGap?: number;
  rainMaxGap?: number;
  rainMinDur?: number;
  rainMaxDur?: number;
  gustMinGap?: number;
  gustMaxGap?: number;
  gustMinDur?: number;
  gustMaxDur?: number;
  /** Fired once at the instant a new storm begins. */
  onRainStart?: () => void;
  /** Cold-snap lifecycle. Warning leads the active phase; active phase
   *  is the freeze-damage window. */
  onColdSnapWarning?: () => void;
  onColdSnapStart?: () => void;
  onColdSnapEnd?: () => void;
  /** Heat-wave lifecycle — same pattern, parallels cold. */
  onHeatWaveWarning?: () => void;
  onHeatWaveStart?: () => void;
  onHeatWaveEnd?: () => void;
  /** In-game days per calendar year — compresses 365 real days into a play
   *  length the user can actually experience. Default 24 → at dayLength 600 s
   *  a full year is ~4 hours. */
  yearLengthDays?: number;
  /** Day-of-year the sim starts on (1..365). Default 113 = April 23. */
  startDayOfYear?: number;
}

export class Weather {
  readonly state: WeatherState;
  private readonly dayLength: number;

  private rainActive = false;
  private rainUntil = 0;
  private rainNextAt = 0;
  private rainTargetIntensity = 0;

  private gustActive = false;
  private gustUntil = 0;
  private gustNextAt = 0;
  private gustTargetStrength = 1;

  private readonly rainMinGap: number;
  private readonly rainMaxGap: number;
  private readonly rainMinDur: number;
  private readonly rainMaxDur: number;
  private readonly gustMinGap: number;
  private readonly gustMaxGap: number;
  private readonly gustMinDur: number;
  private readonly gustMaxDur: number;

  private readonly sunDir = new THREE.Vector3();
  private readonly onRainStart: (() => void) | null;
  private readonly onColdSnapWarning: (() => void) | null;
  private readonly onColdSnapStart: (() => void) | null;
  private readonly onColdSnapEnd: (() => void) | null;
  private readonly onHeatWaveWarning: (() => void) | null;
  private readonly onHeatWaveStart: (() => void) | null;
  private readonly onHeatWaveEnd: (() => void) | null;
  private readonly yearLengthDays: number;

  // Survival-event tuning. One cold snap + one heat wave per in-game
  // year, scheduled in winter / summer windows respectively.
  private static readonly EVENT_WARN_SEC = 30;
  private static readonly EVENT_DUR_SEC = 60;
  private static readonly COLD_SNAP_TEMP_C = -20;
  private static readonly HEAT_WAVE_TEMP_C = 50;
  // Year fractions defining when each event MAY fire.
  // Winter: late Dec → mid Feb (yearT 0.95..1, 0..0.13).
  // Summer: late Jun → late Aug (yearT 0.47..0.66).
  private static readonly COLD_WINDOW_A: [number, number] = [0.95, 1.0];
  private static readonly COLD_WINDOW_B: [number, number] = [0.0, 0.13];
  private static readonly HEAT_WINDOW: [number, number] = [0.47, 0.66];

  private coldYearScheduled = -1;  // year-bucket the current cold event belongs to (-1 = unscheduled)
  private coldSnapTriggerYearT = 0;
  private heatYearScheduled = -1;
  private heatWaveTriggerYearT = 0;
  private prevYearT = 0;

  constructor(opts: WeatherOptions = {}) {
    this.onRainStart = opts.onRainStart ?? null;
    this.onColdSnapWarning = opts.onColdSnapWarning ?? null;
    this.onColdSnapStart = opts.onColdSnapStart ?? null;
    this.onColdSnapEnd = opts.onColdSnapEnd ?? null;
    this.onHeatWaveWarning = opts.onHeatWaveWarning ?? null;
    this.onHeatWaveStart = opts.onHeatWaveStart ?? null;
    this.onHeatWaveEnd = opts.onHeatWaveEnd ?? null;
    this.yearLengthDays = opts.yearLengthDays ?? 24;
    this.dayLength = opts.dayLength ?? 240;
    this.rainMinGap = opts.rainMinGap ?? 60;
    this.rainMaxGap = opts.rainMaxGap ?? 180;
    this.rainMinDur = opts.rainMinDur ?? 15;
    this.rainMaxDur = opts.rainMaxDur ?? 40;
    this.gustMinGap = opts.gustMinGap ?? 8;
    this.gustMaxGap = opts.gustMaxGap ?? 24;
    this.gustMinDur = opts.gustMinDur ?? 2;
    this.gustMaxDur = opts.gustMaxDur ?? 5;

    // Default start: day-of-year 113 = April 23.
    const startDOY = Math.max(1, Math.min(365, opts.startDayOfYear ?? 113));
    const initialYearT = (startDOY - 1) / 365;
    this.state = {
      timeOfDay: opts.initialTimeOfDay ?? 0.35, // start mid-morning
      sunDir: this.sunDir,
      sunIntensity: 0,
      sunColor: new THREE.Color(1, 1, 1),
      ambientIntensity: 0.15,
      skyColor: new THREE.Color(0.3, 0.55, 0.85),
      fogColor: new THREE.Color(0.77, 0.85, 0.91),
      fogDensity: 0.015,
      environmentIntensity: 0.5,
      rain: 0,
      wind: 1,
      yearT: initialYearT,
      dayOfYear: startDOY,
      season: 'spring',
      temperature: 20,
      coldSnapPhase: 'idle',
      coldSnapT: 0,
      heatWavePhase: 'idle',
      heatWaveT: 0,
    };
    this.prevYearT = initialYearT;

    // Schedule first events using the configured cadence so rare-rain settings
    // actually apply to the first storm too.
    this.rainNextAt = this.randRange(this.rainMinGap, this.rainMaxGap);
    this.gustNextAt = 5 + Math.random() * 8;
  }

  update(dt: number, elapsed: number): void {
    const s = this.state;

    // Advance the clock.
    s.timeOfDay = (s.timeOfDay + dt / this.dayLength) % 1;
    // Advance the calendar — yearLengthDays in-game days equals one full year.
    s.yearT = (s.yearT + dt / (this.dayLength * this.yearLengthDays)) % 1;
    s.dayOfYear = Math.max(1, Math.min(365, Math.floor(s.yearT * 365) + 1));
    // Seasons using astronomical-ish breaks (northern hemisphere).
    if (s.dayOfYear < 80 || s.dayOfYear >= 355) s.season = 'winter';
    else if (s.dayOfYear < 172) s.season = 'spring';
    else if (s.dayOfYear < 266) s.season = 'summer';
    else s.season = 'autumn';
    // Temperature: seasonal base (cos peak at summer solstice ≈ yearT 0.47) +
    // daily cycle (cos peak at ~2 PM timeOfDay 0.6).
    const seasonalBase = 12 + 17 * Math.cos(2 * Math.PI * (s.yearT - 0.47));
    const dailyAmp = seasonalBase > 15 ? 10 : 7;
    s.temperature = seasonalBase + dailyAmp * Math.cos(2 * Math.PI * (s.timeOfDay - 0.6));

    // ---- Survival events: cold snap (winter) + heat wave (summer) ----
    this.tickSurvivalEvents(dt, s);

    this.computeSunState(s);

    // Rain scheduler + ramp.
    if (this.rainActive) {
      if (elapsed >= this.rainUntil) {
        this.rainActive = false;
        this.rainTargetIntensity = 0;
        this.rainNextAt = elapsed + this.randRange(this.rainMinGap, this.rainMaxGap);
      }
    } else if (elapsed >= this.rainNextAt) {
      this.rainActive = true;
      this.rainUntil = elapsed + this.randRange(this.rainMinDur, this.rainMaxDur);
      this.rainTargetIntensity = 0.5 + Math.random() * 0.5;
      this.onRainStart?.();
    }
    // Smooth ramp toward target intensity.
    const rainLerp = this.rainActive ? 0.6 * dt : 0.8 * dt;
    s.rain += (this.rainTargetIntensity - s.rain) * Math.min(1, rainLerp * 3);

    // Wind gusts — short bursts above the baseline breeze.
    if (this.gustActive) {
      if (elapsed >= this.gustUntil) {
        this.gustActive = false;
        this.gustTargetStrength = 1;
        this.gustNextAt = elapsed + this.randRange(this.gustMinGap, this.gustMaxGap);
      }
    } else if (elapsed >= this.gustNextAt) {
      this.gustActive = true;
      this.gustUntil = elapsed + this.randRange(this.gustMinDur, this.gustMaxDur);
      this.gustTargetStrength = 2.0 + Math.random() * 2.0;
    }
    const windLerp = this.gustActive ? 5 * dt : 1.8 * dt;
    s.wind += (this.gustTargetStrength - s.wind) * Math.min(1, windLerp);
    // Rain ramps wind up a bit too.
    if (s.rain > 0.1) s.wind = Math.max(s.wind, 1.4 + s.rain * 0.8);
  }

  /**
   * Schedule + tick the once-per-year cold-snap and heat-wave events.
   * Each fires when yearT crosses its trigger value, runs through
   * warning → active → idle, then waits for the next year to schedule
   * a fresh trigger inside the appropriate season window.
   */
  private tickSurvivalEvents(dt: number, s: WeatherState): void {
    const yearBucket = Math.floor(this.elapsedYears(s.yearT));

    // Schedule next cold/heat triggers if we've entered a new year.
    if (this.coldYearScheduled !== yearBucket && s.coldSnapPhase === 'idle') {
      this.coldYearScheduled = yearBucket;
      this.coldSnapTriggerYearT = this.pickColdTriggerYearT();
    }
    if (this.heatYearScheduled !== yearBucket && s.heatWavePhase === 'idle') {
      this.heatYearScheduled = yearBucket;
      this.heatWaveTriggerYearT = this.pickHeatTriggerYearT();
    }

    // Idle → warning when yearT crosses the trigger (handles wraparound).
    if (s.coldSnapPhase === 'idle' && this.crossedTrigger(this.prevYearT, s.yearT, this.coldSnapTriggerYearT)) {
      s.coldSnapPhase = 'warning';
      s.coldSnapT = Weather.EVENT_WARN_SEC;
      this.onColdSnapWarning?.();
    }
    if (s.heatWavePhase === 'idle' && this.crossedTrigger(this.prevYearT, s.yearT, this.heatWaveTriggerYearT)) {
      s.heatWavePhase = 'warning';
      s.heatWaveT = Weather.EVENT_WARN_SEC;
      this.onHeatWaveWarning?.();
    }

    // Tick state machines.
    if (s.coldSnapPhase === 'warning') {
      s.coldSnapT -= dt;
      if (s.coldSnapT <= 0) {
        s.coldSnapPhase = 'active';
        s.coldSnapT = Weather.EVENT_DUR_SEC;
        this.onColdSnapStart?.();
      }
    } else if (s.coldSnapPhase === 'active') {
      s.coldSnapT -= dt;
      s.temperature = Weather.COLD_SNAP_TEMP_C;
      if (s.coldSnapT <= 0) {
        s.coldSnapPhase = 'idle';
        s.coldSnapT = 0;
        this.onColdSnapEnd?.();
      }
    }
    if (s.heatWavePhase === 'warning') {
      s.heatWaveT -= dt;
      if (s.heatWaveT <= 0) {
        s.heatWavePhase = 'active';
        s.heatWaveT = Weather.EVENT_DUR_SEC;
        this.onHeatWaveStart?.();
      }
    } else if (s.heatWavePhase === 'active') {
      s.heatWaveT -= dt;
      s.temperature = Weather.HEAT_WAVE_TEMP_C;
      if (s.heatWaveT <= 0) {
        s.heatWavePhase = 'idle';
        s.heatWaveT = 0;
        this.onHeatWaveEnd?.();
      }
    }

    this.prevYearT = s.yearT;
  }

  /** Approximate "elapsed years" derived from the unwrapped yearT. We
   *  use it as a per-year bucket key; only the integer change matters
   *  so wrapping at 1.0 is fine — the floor still tracks years. */
  private elapsedYears(yearT: number): number {
    // yearT is in [0, 1) — but we need a monotonic bucket. Use the
    // wrap counter implied by prevYearT: when current < prev we wrapped.
    // Simpler: bucket by (yearT < prevYearT ? prevBucket+1 : prevBucket)
    // — track via a side variable.
    if (yearT < this.prevYearT - 0.5) this.yearBucket++;
    return this.yearBucket;
  }
  private yearBucket = 0;

  /** Did yearT (which advances ~monotonically with wrap) cross the
   *  trigger value this tick? Handles year-wraparound. */
  private crossedTrigger(prev: number, cur: number, trigger: number): boolean {
    if (cur >= prev) return prev < trigger && cur >= trigger;
    // Wrapped through 1.0 — split into two ranges.
    return (prev < trigger && trigger <= 1) || (trigger >= 0 && cur >= trigger);
  }

  /** Pick a yearT inside the winter window (Dec 21–Feb 15-ish). The
   *  window straddles the year boundary so we union two sub-ranges. */
  private pickColdTriggerYearT(): number {
    const a = Weather.COLD_WINDOW_A; // [0.95, 1.0]
    const b = Weather.COLD_WINDOW_B; // [0.0, 0.13]
    const aLen = a[1] - a[0];
    const bLen = b[1] - b[0];
    const totalLen = aLen + bLen;
    const r = Math.random() * totalLen;
    return r < aLen ? a[0] + r : b[0] + (r - aLen);
  }
  /** Pick a yearT inside the summer window. */
  private pickHeatTriggerYearT(): number {
    const w = Weather.HEAT_WINDOW;
    return w[0] + Math.random() * (w[1] - w[0]);
  }

  /**
   * Day/night transform: derive sunDir + colour/intensity + fog/sky tint from
   * timeOfDay. Smooth blends through sunrise and sunset so nothing pops.
   */
  private computeSunState(s: WeatherState): void {
    // Sun moves east → west along a tilted arc. Angle: 0 at sunrise, π at sunset.
    const theta = (s.timeOfDay - 0.25) * Math.PI * 2; // -π/2 at midnight, 0 sunrise, π/2 noon, π sunset, 3π/2 next midnight
    // Direction FROM sun TO origin — we flip later when positioning the light.
    // Sun traces a half-circle in the XY plane, tilted back on Z.
    const sunX = Math.cos(theta);
    const sunY = Math.sin(theta);
    const sunZ = 0.25;
    const len = Math.hypot(sunX, sunY, sunZ) || 1;
    this.sunDir.set(-sunX / len, -sunY / len, -sunZ / len);

    // Sun intensity proportional to height above horizon. Smooth through dawn/dusk.
    const altitude = Math.max(0, sunY);
    s.sunIntensity = 6.0 * Math.pow(altitude, 0.8);

    // Warm colour near the horizon, bleached near zenith.
    const horizonMix = Math.max(0, 1 - altitude * 2.5); // 1 at horizon, 0 high up
    s.sunColor.setRGB(
      1.0,
      1.0 - horizonMix * 0.25,
      1.0 - horizonMix * 0.55,
    );

    // Ambient fills a little when sun is down so we're not pitch black.
    const isNight = sunY < 0;
    s.ambientIntensity = isNight
      ? 0.12 + Math.max(0, -sunY * 0.1)
      : 0.15 + altitude * 0.15;

    // Sky colour: cool deep blue at night, warm peach at horizon, day blue.
    const night = new THREE.Color(0.05, 0.06, 0.12);
    const dawn = new THREE.Color(0.95, 0.65, 0.45);
    const day = new THREE.Color(0.45, 0.68, 0.92);
    if (sunY < 0) {
      const t = Math.min(1, -sunY * 2);
      s.skyColor.copy(dawn).lerp(night, t);
    } else {
      const t = Math.min(1, altitude * 2);
      s.skyColor.copy(dawn).lerp(day, t);
    }

    // Fog follows sky, denser at night and during rain.
    s.fogColor.copy(s.skyColor).multiplyScalar(0.9).lerp(new THREE.Color(0.55, 0.6, 0.7), 0.25);
    // Denser haze so objects past ~half the 80 m field blur out — helps
    // framerate by making it visually OK for the distant-cull to kick in.
    s.fogDensity = 0.035 + (isNight ? 0.010 : 0) + s.rain * 0.020;

    // Environment intensity drops at night so shadows + ambient read.
    s.environmentIntensity = isNight ? 0.18 : 0.45 + altitude * 0.3;
  }

  private randRange(lo: number, hi: number): number {
    return lo + Math.random() * (hi - lo);
  }
}
